import React, { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Compact "select all that apply" dropdown. options: [{key,label,hint?,disabled?,badge?}]
export function MultiSelectDropdown({ label, options, selected, onToggle, onSelectAll, placeholder = "Select…", testId, className = "", disabled = false, renderSummary }) {
  const [open, setOpen] = useState(false);
  const picked = options.filter((o) => selected.includes(o.key));
  const summary = renderSummary ? renderSummary(picked) : picked.length === 0 ? placeholder : picked.length <= 2 ? picked.map((o) => o.label).join(", ") : `${picked.length} selected`;
  const enabled = options.filter((o) => !o.disabled);
  const allOn = enabled.length > 0 && enabled.every((o) => selected.includes(o.key));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border-2 bg-white text-left transition-colors ${open ? "border-[#D4AF37]" : "border-[#E9ECEF] hover:border-[#0B132B]/30"} disabled:opacity-60 ${className}`}
          data-testid={testId}
        >
          <div className="min-w-0">
            {label && <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">{label}</div>}
            <div className={`text-xs truncate ${picked.length ? "text-[#0B132B] font-semibold" : "text-[#6C757D]"}`} data-testid={testId ? `${testId}-summary` : undefined}>{summary}</div>
          </div>
          <ChevronDown size={14} className={`shrink-0 text-[#6C757D] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-1.5 rounded-sm border-[#E9ECEF]" data-testid={testId ? `${testId}-menu` : undefined}>
        {onSelectAll && enabled.length > 1 && (
          <button type="button" onClick={() => onSelectAll(!allOn)} className="w-full text-left px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B] hover:bg-[#F8F9FA] rounded-sm" data-testid={testId ? `${testId}-select-all` : undefined}>
            {allOn ? "Clear all" : "Select all"}
          </button>
        )}
        <div className="max-h-72 overflow-y-auto">
          {options.map((o) => {
            const on = selected.includes(o.key);
            return (
              <button
                key={o.key}
                type="button"
                disabled={o.disabled}
                onClick={() => onToggle(o.key, o)}
                className={`w-full flex items-start gap-2 px-2 py-1.5 rounded-sm text-left transition-colors ${o.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F8F9FA]"}`}
                data-testid={testId ? `${testId}-option-${o.key}` : undefined}
              >
                <span className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-[2px] border grid place-items-center ${on ? "bg-[#0B132B] border-[#0B132B] text-white" : "border-[#6C757D]/50 bg-white"}`}>
                  {on && <Check size={10} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-xs font-semibold text-[#0B132B]">
                    <span className="truncate">{o.label}</span>
                    {o.badge && <span className="shrink-0 px-1 py-0.5 rounded-sm bg-[#0B132B] text-[#D4AF37] text-[8px] uppercase tracking-[0.16em] font-bold">{o.badge}</span>}
                  </span>
                  {o.hint && <span className="block text-[10px] text-[#6C757D] leading-snug">{o.hint}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
